// Un control que NAVEGA y por lo tanto es un enlace de verdad —
// ADR-2026-09-08-la-sidebar-navega-con-links, extendido a las cards de la home por
// ADR-2026-09-08-las-cards-de-la-home-tambien-son-links.
//
// El problema que resuelve: media app estaba hecha de `<button onClick={navigate(...)}>`.
// Un botón no tiene destino, así que Cmd/Ctrl+clic no abre nada, el clic del medio
// tampoco, el menú contextual no ofrece "Abrir en pestaña nueva" ni "Copiar dirección",
// y el navegador no muestra la URL al pasar por encima. Todo eso lo da el `href` — y en
// esta app cada modo, cada atajo y cada pantalla YA tienen su URL.
//
// Qué hace con el clic:
//   • Sin modificadores → `preventDefault()` y `onNavegar()`: la navegación de siempre,
//     dentro de la app, sin recargar y con las animaciones de cada pantalla.
//   • Con Cmd / Ctrl / Shift / Alt → NO se toca: ese pedido es para el navegador
//     (pestaña nueva, ventana nueva, descargar) y lo resuelve él con el `href`.
//     Interceptarlo era exactamente lo que rompía el Cmd+clic.
//   • Clic del medio → ni siquiera llega acá (dispara `auxclick`, no `click`), así que
//     funciona solo en cuanto hay `href`.
//
// Sin `href` cae a `<button>`, que es lo correcto para el control que NO navega: el modo
// con candado, que abre el catálogo de planes, o la card que abre un diálogo. Un `<a>`
// sin destino no es un enlace ni para el teclado ni para un lector de pantalla, y encima
// dejaría abrir en otra pestaña algo que el click nunca iba a abrir.
//
// El estilo no cambia al pasar de `<button>` a `<a>`: el preflight de Tailwind deja los
// anchors en `color: inherit` y sin subrayado, así que las clases de cada card alcanzan.

import type { FC, HTMLAttributes, MouseEvent as ReactMouseEvent, ReactNode } from 'react';

export interface PropsEnlaceApp
  extends Omit<HTMLAttributes<HTMLElement>, 'onClick'> {
  /**
   * El destino REAL, para que el navegador tenga a dónde ir. Sin esto el control no
   * navega y se dibuja como botón.
   */
  href?: string;
  /** Qué hace el clic normal, dentro de la app. */
  onNavegar: () => void;
  /** `disabled` del `<button>`; con `href` no aplica (un enlace no se deshabilita). */
  disabled?: boolean;
  children?: ReactNode;
}

export const EnlaceApp: FC<PropsEnlaceApp> = ({
  href,
  onNavegar,
  disabled,
  children,
  ...resto
}) => {
  const alClick = (e: ReactMouseEvent<HTMLElement>) => {
    if (href && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    e.preventDefault();
    onNavegar();
  };
  return href ? (
    <a href={href} onClick={alClick} {...resto}>
      {children}
    </a>
  ) : (
    <button type="button" disabled={disabled} onClick={alClick} {...resto}>
      {children}
    </button>
  );
};

export default EnlaceApp;
